'use client';

import { useState, useEffect } from 'react';
import { Menu, X } from 'lucide-react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import CartButton from './cart-button';

const NAV_ITEMS = [
  { label: 'Maison', href: '#maison' },
  { label: 'Héritage', href: '#heritage' },
  { label: 'Collections', href: '#collections' },
  { label: 'Atelier', href: '#atelier' },
  { label: 'Savoir-Faire', href: '#savoir-faire' },
  { label: 'Lexique', href: '#lexique' },
  { label: 'Salon Privé', href: '#salon-prive' },
  { label: 'Distinctions', href: '#distinctions' },
  { label: 'Galerie', href: '#galerie' },
  { label: 'Témoignages', href: '#temoignages' },
  { label: 'Contact', href: '#contact' },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const pathname = usePathname();

  // Detect if we're on a "shop" page (not the homepage) — header gets solid bg
  const isShopPage = pathname !== '/';

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleNav = (href: string) => {
    setMenuOpen(false);
    if (href.startsWith('#')) {
      const el = document.querySelector(href);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const isSolid = scrolled || isShopPage;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-700 ${
        isSolid
          ? 'bg-[#2B1810]/95 backdrop-blur-md py-3 shadow-[0_8px_32px_rgba(0,0,0,0.4)]'
          : 'bg-transparent py-6'
      }`}
    >
      <div className="container mx-auto px-6 flex items-center justify-between gap-4">
        {/* Logo */}
        <Link
          href="/"
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="flex items-center gap-3 group flex-shrink-0"
        >
          <div className="relative">
            <div className="w-11 h-11 rounded-full border border-[#C9A961] flex items-center justify-center bg-[#2B1810]/60 group-hover:border-[#D4A017] transition-colors duration-500">
              <span className="font-display text-[#C9A961] text-lg font-bold tracking-wider">A</span>
            </div>
            <div className="absolute inset-0 rounded-full border border-[#C9A961]/30 animate-ping opacity-20" />
          </div>
          <div className="flex flex-col items-start leading-none">
            <span className="font-display text-[#F8F1E4] text-lg tracking-[0.3em] font-semibold">
              ASMAO
            </span>
            <span className="font-serif-display text-[#C9A961] text-[10px] tracking-[0.4em] uppercase mt-1">
              Style Cuir
            </span>
          </div>
        </Link>

        {/* Desktop nav (homepage only) */}
        {pathname === '/' && (
          <nav className="hidden lg:flex items-center gap-7">
            {NAV_ITEMS.map((item) => (
              <button
                key={item.href}
                onClick={() => handleNav(item.href)}
                className="relative text-[#F8F1E4]/80 hover:text-[#C9A961] transition-colors duration-300 text-sm tracking-[0.18em] uppercase font-sans font-medium group"
              >
                {item.label}
                <span className="absolute -bottom-2 left-0 w-0 h-px bg-[#C9A961] group-hover:w-full transition-all duration-500" />
              </button>
            ))}
          </nav>
        )}

        {/* Shop nav (visible on all pages) */}
        <div className="hidden lg:flex items-center gap-3">
          <Link
            href="/salon-prive"
            className={`relative px-3 py-2 text-[11px] tracking-[0.2em] uppercase font-medium transition-all duration-300 border ${
              pathname === '/salon-prive'
                ? 'bg-[#2B1810] text-[#C9A961] border-[#C9A961]/40'
                : 'text-[#C9A961]/90 hover:text-[#F4E4BC] border-transparent hover:border-[#C9A961]/30'
            }`}
          >
            Salon Privé
          </Link>
          <Link
            href="/sur-mesure"
            className={`relative px-3 py-2 text-[11px] tracking-[0.2em] uppercase font-medium transition-all duration-300 border ${
              pathname === '/sur-mesure'
                ? 'bg-[#2B1810] text-[#C9A961] border-[#C9A961]/40'
                : 'text-[#C9A961]/90 hover:text-[#F4E4BC] border-transparent hover:border-[#C9A961]/30'
            }`}
          >
            Sur-Mesure
          </Link>
          <Link
            href="/boutique"
            className={`relative px-4 py-2 text-sm tracking-[0.2em] uppercase font-medium transition-all duration-300 border rounded-sm ${
              pathname === '/boutique'
                ? 'bg-[#C9A961] text-[#2B1810] border-[#C9A961]'
                : 'text-[#F8F1E4]/90 hover:text-[#C9A961] border-transparent hover:border-[#C9A961]/40'
            }`}
          >
            Boutique
          </Link>
          <CartButton />
        </div>

        {/* Mobile actions */}
        <div className="flex items-center gap-2 lg:hidden">
          <CartButton />
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className="text-[#F8F1E4] p-2"
            aria-label="Menu"
          >
            {menuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile nav */}
      <div
        className={`lg:hidden overflow-hidden transition-all duration-500 ${
          menuOpen ? 'max-h-[700px] opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <nav className="container mx-auto px-6 py-6 flex flex-col gap-3 bg-[#2B1810]/98 backdrop-blur-md">
          {pathname === '/' &&
            NAV_ITEMS.map((item) => (
              <button
                key={item.href}
                onClick={() => handleNav(item.href)}
                className="text-left text-[#F8F1E4]/90 hover:text-[#C9A961] transition-colors duration-300 text-base tracking-[0.18em] uppercase font-medium py-2 border-b border-[#C9A961]/15"
              >
                {item.label}
              </button>
            ))}
          <Link
            href="/salon-prive"
            onClick={() => setMenuOpen(false)}
            className="text-left text-[#C9A961] hover:text-[#F4E4BC] transition-colors duration-300 text-sm tracking-[0.2em] uppercase font-semibold py-2 border-b border-[#C9A961]/30 flex items-center gap-2"
          >
            Salon Privé
            <span className="text-[9px] bg-[#C9A961]/20 text-[#C9A961] px-2 py-0.5 rounded-full font-bold tracking-[0.15em]">
              VIP
            </span>
          </Link>
          <Link
            href="/sur-mesure"
            onClick={() => setMenuOpen(false)}
            className="text-left text-[#C9A961] hover:text-[#F4E4BC] transition-colors duration-300 text-sm tracking-[0.2em] uppercase font-semibold py-2 border-b border-[#C9A961]/30 flex items-center gap-2"
          >
            Sur-Mesure
            <span className="text-[9px] bg-[#C9A961]/20 text-[#C9A961] px-2 py-0.5 rounded-full font-bold tracking-[0.15em]">
              ATELIER
            </span>
          </Link>
          <Link
            href="/boutique"
            onClick={() => setMenuOpen(false)}
            className="text-left text-[#C9A961] hover:text-[#D4A017] transition-colors duration-300 text-base tracking-[0.2em] uppercase font-semibold py-2 border-b border-[#C9A961]/30 flex items-center gap-2"
          >
            Boutique en Ligne
            <span className="text-[10px] bg-[#C9A961] text-[#2B1810] px-2 py-0.5 rounded-full font-bold">
              NEW
            </span>
          </Link>
        </nav>
      </div>
    </header>
  );
}
